import type { FC, ReactNode } from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'

interface SplitProps {
    text: string
    className?: string
    innerClassName?: string
}

/**
 * Words wrapped in masks for staggered reveals. Screen readers get the plain sentence.
 * Pair with data-reveal="words" on the parent, or animate '.mask > span' yourself.
 */
export const SplitWords: FC<SplitProps> = ({ text, className, innerClassName }): ReactNode => {
    const words = text.split(' ')

    return (
        <>
            <span className='sr-only'>
                {text}
            </span>

            <span aria-hidden='true' className={className}>
                {words.map((word, index) => (
                    <Fragment key={`${word}-${index}`}>
                        <span className='mask'>
                            <span className={innerClassName}>
                                {word}
                            </span>
                        </span>

                        {index < words.length - 1 ? ' ' : null}
                    </Fragment>
                ))}
            </span>
        </>
    )
}

/** Characters wrapped in masks, grouped per word so lines only break between words. */
export const SplitChars: FC<SplitProps> = ({ text, className, innerClassName }): ReactNode => {
    const words = text.split(' ')

    return (
        <>
            <span className='sr-only'>
                {text}
            </span>

            <span aria-hidden='true' className={className}>
                {words.map((word, wordIndex) => (
                    <Fragment key={`${word}-${wordIndex}`}>
                        <span className='inline-block whitespace-nowrap'>
                            {Array.from(word).map((char, charIndex) => (
                                <span key={charIndex} className='mask'>
                                    <span className={cn(innerClassName)}>
                                        {char}
                                    </span>
                                </span>
                            ))}
                        </span>

                        {wordIndex < words.length - 1 ? ' ' : null}
                    </Fragment>
                ))}
            </span>
        </>
    )
}
